import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { CollapsiblePanel } from './CollapsiblePanel';
import './LogoPanel.css';

export const LogoPanel: React.FC = () => {
  const { t } = useTranslation('create');
  const config = useAppStore(state => state.config);
  const setConfig = useAppStore(state => state.setConfig);
  const logoFile = useAppStore(state => state.logoFile);
  const setLogoFile = useAppStore(state => state.setLogoFile);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [dragOver, setDragOver] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleFile = (file: File) => {
    setError(null);
    if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) {
      setError(t('create.logo.errorType', 'Invalid file type.'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t('create.logo.errorSize', 'Logo file must be under 5 MB.'));
      return;
    }
    
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      if (img.width > 4096 || img.height > 4096) {
        setError(t('create.logo.errorDim', 'Logo image dimensions must be at most 4096 × 4096 pixels.'));
        URL.revokeObjectURL(url);
      } else {
        setLogoFile(url);
      }
    };
    img.src = url;
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = () => {
    setDragOver(false);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      handleFile(e.target.files[0]);
    }
  };

  return (
    <CollapsiblePanel title={t('create.logo.title', 'Logo')} defaultExpanded={false}>
      {!logoFile ? (
        <>
          <div 
            className={`logo-dropzone ${dragOver ? 'drag-over' : ''}`}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={24} />
            <span>{t('create.logo.dropzone', 'Drag & drop or click to upload')}</span>
            <input 
              type="file" 
              accept=".png,.jpg,.jpeg,.svg" 
              className="hidden" 
              ref={fileInputRef}
              onChange={onFileChange}
            />
          </div>
          {error && <div className="field-error">{error}</div>}
        </>
      ) : (
        <>
          <div className="logo-preview">
            <span className="logo-filename">{t('create.logo.uploaded', 'Logo uploaded')}</span>
            <button className="logo-remove-btn" onClick={() => { setLogoFile(null); }} aria-label={t('create.logo.remove', 'Remove logo')}>
              <X size={16} />
            </button>
          </div>
          
          <div className="slider-group">
            <div className="slider-header">
              <label>{t('create.logo.size', 'Logo Size')}</label>
              <span>{config.logoSize}%</span>
            </div>
            <input 
              type="range" 
              min={5} max={40} step={1} 
              value={config.logoSize} 
              onChange={e => { setConfig({ logoSize: Number(e.target.value) }); }}
            />
          </div>

          <div className="slider-group">
            <div className="slider-header">
              <label>{t('create.logo.margin', 'Logo Margin')}</label>
              <span>{config.logoMargin} px</span>
            </div>
            <input 
              type="range" 
              min={0} max={10} step={1} 
              value={config.logoMargin} 
              onChange={e => { setConfig({ logoMargin: Number(e.target.value) }); }}
            />
          </div>
        </>
      )}
    </CollapsiblePanel>
  );
};
